import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { comnUtils, comnEnvs } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button, FormControl } from "@/comn/components"; // 화면 구성 컴포넌트
import { useForm, useFetch, useResource, useGrid, useModal, useStore, useToast, usePopup, usePage } from "@/comn/hooks"; // hook
import { BASE, URLS, APIS, SF_CSTM_INFO } from "@/tra/tancis/ptli/usr/cstm/services/PtliCstmMgmtService";

/*
 * Customs Registration
 * !== 세관 등록 ==!
 */
export const PTLI0901002S = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation();
    const navigate = useNavigate();
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast();

    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0016" },
            { area: "comnCd", comnCd: "CLR0064" },
            { area: "comnCd", comnCd: "COM0019" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Customs Search !== 세관 등록 ==!
        cstmInfo: useForm({
            defaultSchema: SF_CSTM_INFO,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Customs code duplicate check !== 세관코드 중복 체크 ==!
        getCstmOfceCd: useFetch({
            api: (data: any) => APIS.getCstmInfo(data),
            enabled: false,
            onSuccess: (data) => {
                if (!data.cstmInfo.content) {
                    form.cstmInfo.clearErrors("cstmOfceCd");
                } else {
                    form.cstmInfo.setError("cstmOfceCd", {
                        message: "msg.com.00106", // The same customs code exists. !== 동일한 세관코드가 존재합니다. ==!
                        type: "error",
                    });
                }
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),

        // Save Repacking Item Application !== 세관 등록 ==!
        saveCstm: useFetch({
            api: (data) => APIS.saveCstm({ ...data }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00062",
                    onConfirm: () => {
                        navigate(URLS.ptli0901001q);
                    },
                    onCancel: () => {
                        navigate(URLS.ptli0901001q);
                    },
                });
            },
            onError: (error) => {
                console.log(error);
                if (error.response.status === 400) {
                    console.log(error.response.data);
                    form.cstmInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: "msg.00006",
                });
            },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Customs code duplicate check !== 세관코드 중복 체크 ==!
        getCstmOfceCd: () => {
            const cstmOfceCd = form.cstmInfo.getValue("cstmOfceCd");
            if (cstmOfceCd) {
                fetch.getCstmOfceCd.fetch(cstmOfceCd);
            } else {
                form.cstmInfo.clearErrors("cstmOfceCd");
            }
        },

        // customs registration !== 세관 등록 ==!
        saveCstm: () => {
            if (!comnUtils.isEmpty(form.cstmInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }
            form.cstmInfo.handleSubmit(
                () => {
                    const data = form.cstmInfo.getValues();
                    modal.openModal({
                        content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                        onConfirm: () => {
                            fetch.saveCstm.fetch(data);
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" });
                },
            )();
        },
    };

    useEffect(() => {
        form.cstmInfo.setFocus("cstmOfceCd");
    }, []);

    return (
        <Page>
            <form>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control
                                    {...form.cstmInfo.schema.cstmOfceCd}
                                    onBlur={() => {
                                        handler.getCstmOfceCd();
                                    }}
                                />
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.cstmInfo.schema.cstmTzNm}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.cstmInfo.schema.cstmEnNm}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.cstmInfo.schema.cstmKoNm}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.cstmInfo.schema.cstmAddr}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.cstmInfo.schema.cstmTelno}></Group.Control>
                                <Group.Control {...form.cstmInfo.schema.cstmFaxNo}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.cstmInfo.schema.scnrYn}></Group.Control>
                                <Group.Control {...form.cstmInfo.schema.dclrYn}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.cstmInfo.schema.cstmTpCd}></Group.Control>
                                <Group.Control {...form.cstmInfo.schema.regnTpCd}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.cstmInfo.schema.regnCd}></Group.Control>
                                <Group.Control {...form.cstmInfo.schema.cstmTlxNo}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.cstmInfo.schema.cstmDesc}></Group.Control>
                            </Group.Row>
                        </Group.Section>
                    </Group.Body>
                </Group>
            </form>
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.ptli0901001q);
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    <Button
                        role="save"
                        onClick={() => {
                            handler.saveCstm();
                        }}
                    ></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
